import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	Input,
	OnDestroy,
	OnInit,
} from '@angular/core';
import { AsyncSubject } from 'rxjs';
import { switchMap, takeUntil } from 'rxjs/operators';
import { ActiveStudentService } from 'src/lib/services/startup/active-student/active-student.service';
import { StudentStoreService } from 'src/lib/services/stores/students/student/student-store.service';
import { TemporalIntlFormatPipe } from '../../../pipes/temporal/temporal-intl-format.pipe';
import { TemporalZonedMapPipe } from '../../../pipes/temporal/temporal-zoned-map.pipe';

@Component({
	selector: 'ae-active-student-time',
	templateUrl: './active-student-time.component.html',
	styleUrls: ['./active-student-time.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [TemporalIntlFormatPipe, TemporalZonedMapPipe],
})
export class ActiveStudentTimeComponent implements OnInit, OnDestroy {
	private _unsubscribe$ = new AsyncSubject<null>();

	@Input() public ignoreYearIfCurrent = false;
	@Input() public date: Date;
	@Input() public dateFormat: Intl.DateTimeFormatOptions = {
		month: '2-digit',
		day: '2-digit',
		year: 'numeric',
	};

	public tz: string;
	public readonly numericUs: Intl.DateTimeFormatOptions = {
		month: '2-digit',
		day: '2-digit',
		year: 'numeric',
	};

	constructor(
		private activeStudent: ActiveStudentService,
		private studentStoreService: StudentStoreService,
		private cdr: ChangeDetectorRef,
	) {}

	ngOnInit(): void {
		this.tz = Temporal.Now.timeZoneId();

		this.activeStudent
			.getActiveStudent$()
			.pipe(
				switchMap((studentId) => this.studentStoreService.overview$(studentId)),
				takeUntil(this._unsubscribe$),
			)
			.subscribe((overview) => {
				this.tz = overview.institute_timezone;
				this.cdr.detectChanges();
			});
	}

	public get computedDateFormat(): Intl.DateTimeFormatOptions {
		if (!this.ignoreYearIfCurrent || !this.date) {
			return this.dateFormat;
		}

		return this.createDateFormat(this.date);
	}

	protected createDateFormat = (date: Date) => {
		const currentYear = Temporal.Now.plainDateISO().year;

		const instant = date.toTemporalInstant();

		const timezone = this.tz || Temporal.Now.timeZoneId();

		if (instant.toZonedDateTimeISO(timezone).year === currentYear) {
			return {
				month: 'short',
				day: 'numeric',
				weekday: 'short',
			} as Intl.DateTimeFormatOptions;
		}
		return {
			month: 'short',
			day: 'numeric',
			year: 'numeric',
			weekday: 'short',
		} as Intl.DateTimeFormatOptions;
	};

	ngOnDestroy(): void {
		this._unsubscribe$.next(null);
		this._unsubscribe$.complete();
		this._unsubscribe$ = null;
	}
}
